/* WizardPaymentCommitment.jsx — Self-Plan Step 6 payment-commitment card.
 *
 * iter98.99de — Doctor flagged that the CoE-Plan flow's ApprovePayDialog
 *   asks for a payment commitment (Pay later / Pay advance / Pay in full)
 *   but the Self-Plan submit step lacks it. This component fills that gap.
 *
 * Mirrors the ApprovePayDialog UI down to copy + iconography so doctors
 * see the same chrome no matter which flow they took. The choice is
 * captured into wizard `pricing.payment_mode` + `pricing.advance_amount`
 * and consumed by submitCase() — after POST /cases/{id}/submit succeeds,
 * if mode != "later" the wizard POSTs to /cases/{id}/advance-intent so
 * the lab kicks off with the advance intent already minted.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
    Wallet, Clock, Lightning, CheckCircle, Receipt,
} from "@phosphor-icons/react";


function inr(n) {
    if (n == null || n === "") return "₹0";
    const v = Number(n);
    if (!Number.isFinite(v)) return "₹0";
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
}


const MODES = [
    {
        id: "later",
        title: "Pay later",
        subtitle: "Settle from your dashboard after delivery",
        Icon: Clock,
        accent: "slate",
    },
    {
        id: "advance",
        title: "Pay advance",
        subtitle: "Commit a partial amount upfront",
        Icon: Wallet,
        accent: "amber",
    },
    {
        id: "full",
        title: "Pay in full",
        subtitle: "Settle 100% upfront",
        Icon: Lightning,
        accent: "emerald",
    },
];


const ACCENTS = {
    slate:   { ring: "ring-slate-300",   bg: "bg-slate-50",   text: "text-slate-700",   chip: "bg-slate-100 text-slate-700"   },
    amber:   { ring: "ring-amber-300",   bg: "bg-amber-50",   text: "text-amber-800",   chip: "bg-amber-100 text-amber-800"   },
    emerald: { ring: "ring-emerald-300", bg: "bg-emerald-50", text: "text-emerald-800", chip: "bg-emerald-100 text-emerald-800" },
};


export default function WizardPaymentCommitment({ pricing, setPricing, finalCharge }) {
    const total = Number(finalCharge || 0)
        || Math.max(0,
            Number(pricing?.lab_charge || 0)
            + Number(pricing?.priority_fee || 0)
            + Number(pricing?.scanner_pickup_fee || 0)
            - Number(pricing?.discount_amount || 0)
        );

    const [mode, setMode] = useState(() => pricing?.payment_mode || "later");
    const [advance, setAdvance] = useState(() => {
        const seed = Number(pricing?.advance_amount || 0);
        if (seed > 0) return String(Math.round(seed));
        // Default suggestion: 30% of total, rounded to the nearest ₹1,000.
        const suggested = Math.max(0, Math.round((total * 0.3) / 1000) * 1000);
        return suggested ? String(suggested) : "";
    });

    /* Persist whenever local UI state changes so the wizard's submitCase()
       has the latest commitment when it fires. `pct` is derived from the
       amount + total so the backend gets it in one shot. */
    useEffect(() => {
        const amt = mode === "full"
            ? total
            : mode === "advance"
                ? Math.max(0, Math.min(total, Number(advance || 0)))
                : 0;
        const pct = total > 0 ? Math.round((amt / total) * 10000) / 100 : 0;
        setPricing((p) => ({
            ...(p || {}),
            payment_mode: mode,
            advance_amount: amt,
            advance_pct: pct,
        }));
    }, [mode, advance, total, setPricing]);

    const quickChips = useMemo(() => {
        if (total <= 0) return [];
        const round = (n) => Math.max(0, Math.round(n / 1000) * 1000);
        return [
            { label: "30%", value: round(total * 0.30) },
            { label: "50%", value: round(total * 0.50) },
            { label: "75%", value: round(total * 0.75) },
        ];
    }, [total]);

    const commitAmount = mode === "full"
        ? total
        : mode === "advance"
            ? Math.max(0, Math.min(total, Number(advance || 0)))
            : 0;

    return (
        <div
            className="rounded-2xl border-2 border-border bg-card overflow-hidden"
            data-testid="case-wizard-payment-commitment"
        >
            <div className="px-5 py-3.5 border-b border-border/60 bg-secondary/30 flex items-center gap-2.5">
                <Wallet size={16} className="text-foreground/80" weight="duotone" />
                <div className="flex-1">
                    <h3 className="font-display text-[14px] font-semibold leading-tight">Payment commitment</h3>
                    <p className="text-[11.5px] text-muted-foreground leading-snug">
                        Decide how you&apos;d like to settle the lab charge. You can change this later.
                    </p>
                </div>
                <span className="text-[11px] text-muted-foreground hidden sm:inline">Total: <span className="font-semibold text-foreground tabular-nums">{inr(total)}</span></span>
            </div>

            <div className="px-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {MODES.map((m) => {
                        const a = ACCENTS[m.accent];
                        const active = mode === m.id;
                        return (
                            <button
                                type="button"
                                key={m.id}
                                onClick={() => setMode(m.id)}
                                className={`text-left rounded-xl border-2 px-3.5 py-3 transition-colors ${active ? `border-foreground ${a.bg}` : "border-border bg-card hover:border-primary/30"}`}
                                data-testid={`case-wizard-payment-mode-${m.id}`}
                            >
                                <div className="flex items-start gap-2.5">
                                    <div className={`shrink-0 w-8 h-8 rounded-lg ring-1 grid place-items-center ${active ? `${a.chip} ${a.ring}` : "bg-secondary/60 ring-border text-muted-foreground"}`}>
                                        <m.Icon size={14} weight={active ? "fill" : "regular"} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold leading-tight">{m.title}</div>
                                        <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{m.subtitle}</div>
                                    </div>
                                    {active && <CheckCircle size={14} className={a.text} weight="fill" />}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {mode === "advance" && (
                    <div
                        className="mt-3.5 rounded-xl border border-amber-200 bg-amber-50/60 px-3.5 py-3"
                        data-testid="case-wizard-payment-advance-block"
                    >
                        <label className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-amber-800/90">Advance amount</label>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <div className="flex items-center rounded-lg border-[0.5px] border-amber-300 bg-card overflow-hidden focus-within:border-amber-500 max-w-[200px]">
                                <span className="text-[12px] font-semibold px-2.5 text-amber-800/80 border-r border-amber-300/60">₹</span>
                                <Input
                                    type="number" min="0" max={total} step="500"
                                    value={advance}
                                    onChange={(e) => setAdvance(e.target.value)}
                                    placeholder="0"
                                    className="h-9 border-0 focus-visible:ring-0 text-[16px] font-bold p-0 px-2 bg-transparent w-full tabular-nums"
                                    data-testid="case-wizard-payment-advance-amount"
                                />
                            </div>
                            <div className="flex items-center gap-1.5">
                                {quickChips.map((c) => (
                                    <button
                                        type="button"
                                        key={c.label}
                                        onClick={() => setAdvance(String(c.value))}
                                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border-[0.5px] transition-colors ${Number(advance) === c.value ? "border-amber-500 bg-amber-100 text-amber-900" : "border-amber-300/60 bg-card text-amber-800 hover:bg-amber-100/60"}`}
                                        data-testid={`case-wizard-payment-quick-${c.label}`}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {total > 0 && Number(advance) > 0 && (
                            <div className="mt-2 text-[11px] text-amber-900/85 leading-snug">
                                That&apos;s <span className="font-bold tabular-nums">{Math.round((Math.min(total, Number(advance)) / total) * 100)}%</span> upfront, balance <span className="font-semibold tabular-nums">{inr(Math.max(0, total - Number(advance)))}</span> billed on delivery.
                            </div>
                        )}
                    </div>
                )}

                {/* On-submit summary line — mirrors ApprovePayDialog's tail copy. */}
                <div
                    className="mt-3 flex items-start gap-2 text-[11.5px] leading-snug text-muted-foreground"
                    data-testid="case-wizard-payment-summary"
                >
                    <Receipt size={13} weight="duotone" className="shrink-0 mt-0.5 text-foreground/60" />
                    <span>
                        {mode === "later" && <>No upfront commitment. You&apos;ll see the invoice on your <span className="font-medium text-foreground">Billing</span> tab once the lab confirms manufacturing.</>}
                        {mode === "advance" && commitAmount > 0 && <>On Submit we&apos;ll mint a secure pay-link for <span className="font-semibold text-foreground tabular-nums">{inr(commitAmount)}</span>. The lab kicks off without waiting for payment to clear.</>}
                        {mode === "advance" && commitAmount === 0 && <>Enter an advance amount above to mint a pay-link on Submit.</>}
                        {mode === "full" && total > 0 && <>On Submit we&apos;ll mint a secure pay-link for the full <span className="font-semibold text-foreground tabular-nums">{inr(total)}</span>. Zero balance after delivery.</>}
                        {mode === "full" && total === 0 && <>Total is ₹0 — no payment link needed.</>}
                    </span>
                </div>
            </div>
        </div>
    );
}
